import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { callAuthedSupabaseFunction } from '../../lib/api'
import { supabase } from '../../lib/supabase'
import type { PublicSquareConnection } from '../../lib/square-oauth'

function statusLabel(status: PublicSquareConnection['status'] | 'loading') {
  if (status === 'connected') return 'Connected'
  if (status === 'pending_location') return 'Connected — choose location'
  if (status === 'setup_required') return 'Not connected / not configured'
  return 'Not connected'
}

export function SquareConnectionPanel({
  onConnectionChange,
}: Readonly<{ onConnectionChange?: (connection: PublicSquareConnection | null) => void }>) {
  const [connection, setConnection] = useState<PublicSquareConnection | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const loadStatus = useCallback(async () => {
    setError(null)
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) {
      setError('Sign in again to manage the Square connection.')
      return
    }
    const response = await callAuthedSupabaseFunction('square-connection', accessToken)
    const payload = await response.json() as PublicSquareConnection & { ok?: boolean; error?: string }
    if (!payload.ok) {
      setError(payload.error || 'Unable to load the Square connection.')
      return
    }
    setConnection(payload)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const square = params.get('square')
    if (square === 'error') {
      setError(params.get('reason') || 'Square authorisation did not complete.')
    } else if (square === 'connected') {
      setMessage('Square authorised. Choose the Reader location if more than one exists.')
    } else if (square === 'select_location') {
      setMessage('Square authorised more than one location. Choose the studio location.')
    }
    void loadStatus()
  }, [loadStatus])

  useEffect(() => {
    onConnectionChange?.(connection)
  }, [connection, onConnectionChange])

  async function authedPost(name: string, body: Record<string, unknown>) {
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) throw new Error('Sign in again to manage the Square connection.')
    const response = await callAuthedSupabaseFunction(name, accessToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const payload = await response.json() as PublicSquareConnection & { ok?: boolean; error?: string; authorizeUrl?: string }
    if (!payload.ok) throw new Error(payload.error || 'Square request failed.')
    return payload
  }

  async function connectSquare() {
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('square-oauth-start', {})
      if (!payload.authorizeUrl) throw new Error('Square did not return an authorisation URL.')
      window.location.assign(payload.authorizeUrl)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Square authorisation could not start.')
      setBusy(false)
    }
  }

  async function selectLocation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const locationId = new FormData(event.currentTarget).get('locationId')
    if (typeof locationId !== 'string' || !locationId) return
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('square-connection', { action: 'select_location', locationId })
      setConnection(payload)
      setMessage('Square location saved.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The Square location was not selected.')
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('square-connection', { action: 'disconnect' })
      setConnection(payload)
      setMessage('Square has been disconnected.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Square could not be disconnected.')
    } finally {
      setBusy(false)
    }
  }

  const status = connection?.status ?? 'not_connected'

  return (
    <article className="admin-card">
      <div className="admin-card-heading">
        <div>
          <h2>Square</h2>
          <p>
            {connection?.locationName || 'No location connected'}
            {connection?.environment ? ` · ${connection.environment}` : ''}
          </p>
        </div>
        <span className="status-pill">{statusLabel(status)}</span>
      </div>
      {message ? <p className="admin-alert">{message}</p> : null}
      {error ? <p className="admin-alert error">{error}</p> : null}
      {connection?.instructions && status === 'setup_required' ? (
        <p className="admin-empty-state">{connection.instructions}</p>
      ) : null}
      {status === 'pending_location' && (connection?.locations?.length ?? 0) > 0 ? (
        <form className="admin-edit-grid" onSubmit={(event) => void selectLocation(event)}>
          <label>
            Square location
            <select defaultValue="" name="locationId" required>
              <option value="" disabled>Select location</option>
              {connection?.locations?.map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </label>
          <button disabled={busy} type="submit">Use this location</button>
        </form>
      ) : null}
      <p className="admin-empty-state">
        Remaining balances are taken onsite through Square Point of Sale and the studio Reader.
        Autoglow does not copy the service catalogue into Square.
      </p>
      <div className="booking-contact-actions">
        {status === 'setup_required' || status === 'not_connected' || status === 'disconnected' ? (
          <button disabled={busy || status === 'setup_required'} onClick={() => void connectSquare()} type="button">
            Connect Square
          </button>
        ) : (
          <>
            <button disabled={busy} onClick={() => void connectSquare()} type="button">Reconnect Square</button>
            <button disabled={busy} onClick={() => void disconnect()} type="button">Disconnect</button>
          </>
        )}
      </div>
    </article>
  )
}
